import React, { useEffect, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import type { MessageStudentsProps as MessageStudentsType } from '../modules/MessageStudents';
import type { StudentType } from '../../types';
import type { AllColorType, GenderType } from '../../../util/types';
import { CollapseSection } from '../modules';
import { CongratulateButton, InfoButton, StudentsTable } from '../../../global';
import { Alert, Button, Collapse, Loader, Tooltip } from '../../../components';
import { mapGroupReportForLastMonday } from '../../../mapper/groupMapper';
import type { GroupReportInterface } from '@whizz/api-model';
import StudentIcon from '../../../components/charts/student-scatter/components/StudentIcon';
import styled from 'styled-components';
import { getPolishedColors } from '../../../util/styled';
import { useAnimateIndex } from '../../../hooks/useAnimateIndex';
import type { SectionProps } from '../Home';

interface Props extends SectionProps {
  /**
   * Handles click on "Message them" button
   * in order to show message modal.
   */
  onMessageStudents(students: MessageStudentsType['students'], type: MessageStudentsType['type']): void;
}

type HallOfFameStudents = {
  inHallOfFame: StudentType[],
  notInHallOfFame: StudentType[],
};

interface IHallOfFameComponent {
  data: HallOfFameStudents,
  week: 'THIS_WEEK' | 'LAST_WEEK';
  info?: string,
  onMessageStudents?: Props['onMessageStudents']
}

const genders: Record<number, GenderType> = {
  1: 'male',
  2: 'female',
  3: 'other',
};

const HallOfFame = ({ isLoading, groupReport, onMessageStudents, open, setOpen }: Props): JSX.Element => {
  const { translate } = useI18n();

  const [ dataLastMonday, setDataLastMonday ] = useState<StudentType[]>([]);

  useEffect(() => {
    if (null == groupReport) {
      return;
    }

    setDataLastMonday(mapGroupReportForLastMonday(groupReport));
  }, [ groupReport ]);

  const getStudentsInHallOfFame = ({ data, hallOfFame, period }: { data: StudentType[], hallOfFame: GroupReportInterface['hallOfFame'], period: 'now' | 'previous' }): [ HallOfFameStudents['inHallOfFame'], HallOfFameStudents['notInHallOfFame'] ] => {
    const hallOfFameData: HallOfFameStudents = {
      inHallOfFame: [],
      notInHallOfFame: [],
    };

    data.forEach((student) => {
      if (hallOfFame && hallOfFame[period] && hallOfFame[period]?.inHallOfFame.includes(student.id)) {
        hallOfFameData.inHallOfFame.push(student);
        return;
      }
      hallOfFameData.notInHallOfFame.push(student);
    });

    return [ hallOfFameData.inHallOfFame, hallOfFameData.notInHallOfFame ];
  };

  const [ inHallOfFame, notInHallOfFame ] = getStudentsInHallOfFame({ data: dataLastMonday, hallOfFame: groupReport!.hallOfFame, period: 'now' });
  const [ previous_inHallOfFame, previous_notInHallOfFame ] = getStudentsInHallOfFame({ data: dataLastMonday, hallOfFame: groupReport!.hallOfFame, period: 'previous' });

  return (
    <CollapseSection
      headline={isLoading ? '' : translate('pages.HOME.HALL_OF_FAME.HEADLINE')}
      strapline={isLoading ? '' : translate('pages.HOME.HALL_OF_FAME.STRAPLINE')}
      dataCy={'home_hof'}
      open={open}
      setOpen={setOpen}
    >
      {isLoading
        ? (<Loader />)
        : (<>
          <div>
            <HallOfFameComponent
              week={'THIS_WEEK'}
              info={'pages.HOME.HALL_OF_FAME.INFO'}
              data={{
                inHallOfFame: inHallOfFame,
                notInHallOfFame: notInHallOfFame,
              }}
              onMessageStudents={onMessageStudents}
            />
            <hr className='mb-md'/>
            <HallOfFameComponent
              week={'LAST_WEEK'}
              data={{
                inHallOfFame: previous_inHallOfFame,
                notInHallOfFame: previous_notInHallOfFame,
              }}
              onMessageStudents={onMessageStudents}
            />
          </div>
        </>)
      }
    </CollapseSection>
  );
};

const HallOfFameComponent = ({ week, data, info, onMessageStudents }: IHallOfFameComponent) => {

  const { translate } = useI18n();
  const [ showInfo, setShowInfo ] = useState<boolean>(false);
  const [ showFullDetails, setShowFullDetails ] = useState<boolean>(false);
  const [ animateIndex ] = useAnimateIndex(data.inHallOfFame);

  return (
    <div className={'mb-md'} data-cy={`hof_${week.toLowerCase()}`}>
      <div className={'flex items-center mb-md h-8'}>
        <h2 className='font-bold'>{translate(`pages.HOME.HALL_OF_FAME.${week}.DESCRIPTION`)}</h2>
        { info ? <InfoButton onClick={() => setShowInfo(!showInfo)} /> : null }
      </div>
      { info ? <Alert disableIcon show={showInfo} className={'my-md'}>
        {translate(info)}
      </Alert> : null }
      <StyledWrapper className={'metrics grid gap-2 grid-cols-auto-1fr items-center p-sm mb-md rounded-default'} $color={'header'}>
        <div className={'flex items-center justify-center rounded-default p-1 bg-header text-2xl md:text-5xl font-bold'}>{data.inHallOfFame.length}</div>
        <div className={'text-md md:text-2xl font-bold'}>{translate(`pages.HOME.HALL_OF_FAME.${week}.IN_HALL_OF_FAME`, { count: data.inHallOfFame.length })}</div>
        <div className={'col-span-2 md:col-start-2 md:col-span-1 flex flex-wrap gap-1'}>
          {data.inHallOfFame.map((student, index) => (
            <div className={'mt-md'} key={`in_hall_of_fame-${student.id}`}>
              <Tooltip
                key={`tooltip_hall_of_fame-${student.id}`}
                tooltipMessage={student.fullName}
              >
                <StudentIcon
                  size={'lg'}
                  color={genders[student.gender ?? 1] as GenderType}
                  animate={animateIndex === index}
                  crown
                />
              </Tooltip>
            </div>
          ))}
        </div>
        <div>
          <div className={'flex items-center justify-center rounded-default p-1  bg-white text-2xl md:text-5xl font-bold'}>{data.notInHallOfFame.length}</div>
        </div>
        <div className={'text-md md:text-2xl font-bold'}>{translate(`pages.HOME.HALL_OF_FAME.${week}.NOT_IN_HALL_OF_FAME`, { count: data.notInHallOfFame.length })}</div>
      </StyledWrapper>
      <div>
        <div className='flex justify-center'>
          <Button
            isOutline
            color={'positive'}
            icon={'ViewList'}
            toggle={showFullDetails}
            isBold
            size={'lg'}
            onClick={() => setShowFullDetails((prevState) => !prevState)}
          >{translate('components.BUTTONS.FULL_DETAILS')}</Button>
        </div>
        <Collapse when={showFullDetails}>
          <div className={'container mt-md border-2 border-positive rounded-md space-y-12'}>
            { !!data.inHallOfFame.length && (
              <section>
                <div className={'flex justify-between items-center mb-md font-bold uppercase'}>
                  {translate(`pages.HOME.HALL_OF_FAME.${week}.table.IN_HALL_OF_FAME`)}
                  {onMessageStudents ? <CongratulateButton onClick={() => onMessageStudents(data.inHallOfFame, `hall_of_fame_${week.toLocaleLowerCase()}` as MessageStudentsType['type'])} /> : null}
                </div>
                <StudentsTable
                  id={'inHallOfFame'}
                  data={data.inHallOfFame}
                  column={[ 'name' ]}
                  color={'positive'}
                />
              </section>
            )}
            { !!data.notInHallOfFame.length && (
              <section>
                <div className={'flex justify-between items-center mb-md font-bold uppercase'}>
                  {translate(`pages.HOME.HALL_OF_FAME.${week}.table.NOT_IN_HALL_OF_FAME`)}
                  {onMessageStudents ? <CongratulateButton onClick={() => onMessageStudents(data.notInHallOfFame, `not_in_hall_of_fame_${week.toLocaleLowerCase()}` as MessageStudentsType['type'])} /> : null}
                </div>
                <StudentsTable
                  id={'notInHallOfFame'}
                  data={data.notInHallOfFame}
                  column={[ 'name' ]}
                  color={'negative'}
                />
              </section>
            )}
          </div>
        </Collapse>
      </div>
    </div>
  );
};

const StyledWrapper = styled('div')<{ $color: AllColorType }>`
  background: ${(props) => getPolishedColors(props).light.color};
`;

HallOfFame.displayName = 'HallOfFame';

export default HallOfFame;
